import { motion } from 'framer-motion'
import { Pen, Share2, Palette, Megaphone, ArrowRight, Code } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../../context/LanguageContext'

const Services = () => {
  const { t } = useLanguage()

  const services = [
    {
      icon: Palette,
      key: 'webDevelopment',
      gradient: 'from-gold-400 to-bronze-500',
      title: 'Web Development',
      description: 'Custom websites and web applications built with modern technologies'
    },
    {
      icon: Share2,
      key: 'socialMedia',
      gradient: 'from-bronze-400 to-gold-600',
      title: 'Social Media Management',
      description: 'Strategic social media management to grow your online presence'
    },
    {
      icon: Pen,
      key: 'contentCreation',
      gradient: 'from-gold-500 to-bronze-400',
      title: 'Content Creation',
      description: 'Engaging content that tells your brand story and connects with your audience'
    },
    {
      icon: Megaphone,
      key: 'logoDesign',
      gradient: 'from-bronze-500 to-gold-500',
      title: 'Logo Design',
      description: 'Unique brand identities and memorable logo designs'
    }
  ]

  return (
    <section className="py-24 bg-gradient-to-br from-bronze-950 to-dark relative overflow-hidden">
      {/* Animated Background Shapes */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 90, 0],
          opacity: [0.03, 0.08, 0.03]
        }}
        transition={{ duration: 20, repeat: Infinity }}
        className="absolute top-20 right-20 w-96 h-96 bg-gradient-gold rounded-full blur-3xl"
      />
      <motion.div
        animate={{
          scale: [1, 1.3, 1],
          rotate: [0, -90, 0],
          opacity: [0.03, 0.08, 0.03]
        }}
        transition={{ duration: 25, repeat: Infinity, delay: 5 }}
        className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-bronze rounded-full blur-3xl"
      />
      
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-gold-500/5 to-transparent opacity-50"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="section-title">
            {t('services.title')} <span className="text-gradient-gold">{t('services.titleHighlight')}</span>
          </h2>
          <p className="text-xl text-beige-200 mt-4 max-w-2xl mx-auto">
            {t('services.subtitle')}
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -10, scale: 1.02 }}
              className="card-premium group cursor-pointer"
            >
              {/* Icon */}
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-6 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-gold`}>
                <service.icon className="w-8 h-8 text-white" />
              </div>

              {/* Content */}
              <h3 className="text-2xl font-display font-bold text-cream mb-4">
                {service.title}
              </h3>
              <p className="text-beige-200 leading-relaxed mb-6">
                {service.description}
              </p>

              {/* Link */}
              <Link
                to="/services"
                className="inline-flex items-center space-x-2 text-gold-600 font-semibold group-hover:text-bronze-600 transition-colors"
              >
                <span>{t('common.learnMore')}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
              </Link>

              {/* Hover Gradient Bar */}
              <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${service.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 rounded-b-2xl`}></div>
            </motion.div>
          ))}
        </div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Link to="/services" className="btn-primary inline-flex items-center space-x-2">
            <span>{t('common.learnMore')}</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  )
}

export default Services
